/**
 * Token / session storage abstraction.
 * Access + refresh tokens live in memory and optionally survive reloads
 * (localStorage when Remember me, otherwise sessionStorage).
 */

import axios from 'axios'
import { API_BASE_URL, STORAGE_KEYS } from '@/constants/app'
import type { ApiEnvelope } from '@/types/api.types'

export type TokenBundle = {
  accessToken?: string
  refreshToken?: string
  expiresAt?: string
}

const MEMORY_KEY = '__pmms_tokens__'

type MemoryStore = TokenBundle & { remember?: boolean }

declare global {
  interface Window {
    [MEMORY_KEY]?: MemoryStore
  }
}

function memory(): MemoryStore {
  if (!window[MEMORY_KEY]) window[MEMORY_KEY] = {}
  return window[MEMORY_KEY]!
}

function persistBucket(remember: boolean): Storage {
  return remember ? localStorage : sessionStorage
}

function clearPersisted(): void {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKENS)
  sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKENS)
}

export const tokenStorage = {
  /** Load tokens from session/local storage into memory (call on boot). */
  hydrate(): void {
    const raw =
      sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKENS) ??
      localStorage.getItem(STORAGE_KEYS.AUTH_TOKENS)
    if (!raw) return
    try {
      const parsed = JSON.parse(raw) as TokenBundle & { remember?: boolean }
      const store = memory()
      store.accessToken = parsed.accessToken
      store.refreshToken = parsed.refreshToken
      store.expiresAt = parsed.expiresAt
      store.remember = Boolean(
        parsed.remember || localStorage.getItem(STORAGE_KEYS.AUTH_TOKENS),
      )
    } catch {
      clearPersisted()
    }
  },

  getAccessToken(): string | undefined {
    return memory().accessToken
  },

  getRefreshToken(): string | undefined {
    return memory().refreshToken
  },

  getExpiresAt(): string | undefined {
    return memory().expiresAt
  },

  getRemember(): boolean {
    return Boolean(memory().remember)
  },

  setTokens(tokens: TokenBundle, remember = false): void {
    const store = memory()
    store.accessToken = tokens.accessToken
    store.refreshToken = tokens.refreshToken
    store.expiresAt = tokens.expiresAt
    store.remember = remember

    clearPersisted()
    persistBucket(remember).setItem(
      STORAGE_KEYS.AUTH_TOKENS,
      JSON.stringify({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiresAt: tokens.expiresAt,
        remember,
      }),
    )
  },

  clear(): void {
    window[MEMORY_KEY] = {}
    clearPersisted()
  },

  /**
   * Silent refresh via raw Axios (bypasses apiClient interceptors).
   */
  async refreshAccessToken(): Promise<TokenBundle | null> {
    const refreshToken = this.getRefreshToken()
    if (!refreshToken) return null

    try {
      const res = await axios.post<
        ApiEnvelope<{
          accessToken: string
          refreshToken: string
          expiresAt: string
        }>
      >(
        `${API_BASE_URL}/auth/refresh`,
        { refreshToken },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          timeout: 30_000,
        },
      )

      const envelope = res.data
      if (!envelope.success || !envelope.data?.accessToken) return null

      const next: TokenBundle = {
        accessToken: envelope.data.accessToken,
        refreshToken: envelope.data.refreshToken,
        expiresAt: envelope.data.expiresAt,
      }
      this.setTokens(next, this.getRemember())
      return next
    } catch {
      return null
    }
  },
}
